import * as AWS from 'aws-sdk';
import { getAuthConfig } from '../config/auth-config';
import { authenticateWithCognito, getOpenIdToken } from './cognito-auth';

let cachedCredentials: AWS.Credentials | null = null;

const REFRESH_BUFFER_MS = 5 * 60 * 1000; // 5 minutes before expiry

function isCredentialValid(): boolean {
  if (!cachedCredentials || !cachedCredentials.expireTime) return false;
  return cachedCredentials.expireTime.getTime() > Date.now() + REFRESH_BUFFER_MS;
}

export function clearCachedCredentials(): void {
  cachedCredentials = null;
}

export async function getAwsCredentials(): Promise<AWS.Credentials> {
  if (isCredentialValid()) {
    return cachedCredentials!;
  }

  let retryCount = 0;
  const maxRetries = 1;

  while (retryCount <= maxRetries) {
    try {
      await authenticateWithCognito();

      const config = getAuthConfig();
      const openIdToken = getOpenIdToken();

      if (!openIdToken) {
        throw new Error('No OpenID token available');
      }

      AWS.config.region = config.region;
      AWS.config.credentials = new AWS.CognitoIdentityCredentials({
        IdentityPoolId: config.identityPoolId,
        Logins: {
          [`cognito-idp.${config.region}.amazonaws.com/${config.userPoolId}`]: openIdToken,
        },
      });

      await (AWS.config.credentials as any).getPromise();
      cachedCredentials = AWS.config.credentials as AWS.Credentials;
      return cachedCredentials;
    } catch (error) {
      retryCount++;
      if (retryCount > maxRetries) {
        cachedCredentials = null;
        throw error;
      }
      // Clear tokens and retry
      document.cookie = 'id_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
      document.cookie = 'openid_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
      cachedCredentials = null;
    }
  }

  throw new Error('Failed to get AWS credentials after retries');
}
